import { useEffect } from 'react'
import { useSearchParams } from 'react-router'

import { viewFromAddress, withView } from './tableAddress'
import { viewKey, type TableColumn } from './tableView'
import { useTableView, type TableView } from './useTableView'

/**
 * A list's column view kept in its address (TRKC-110), beside the list's own filters: the books,
 * the posting board, the load book and the alerts.
 *
 * <p>
 * The headings and boxes answer from state the page owns, with the address as its copy — the
 * board's lesson (TRKC-90): the router applies an address change inside a transition, so a box
 * fed straight from the address showed an older value for a moment, and typing ran over it.
 * The address can also change under the page — the menu's plain link, the browser's Back —
 * and the view follows it. An address that only catches up with what was typed equals the
 * view on screen, and changes nothing: the router's updates are transitions, and React applies
 * the latest of several at once, so typing is never set back to an earlier key (checked in a
 * real browser typing key by key — `e2e/tests/brokers.spec.ts`).
 * </p>
 */
export function useAddressTableView<Row>(
  rows: readonly Row[],
  columns: readonly TableColumn<Row>[],
  rank?: (row: Row) => number,
): TableView<Row> {
  const [searchParams, setSearchParams] = useSearchParams()
  const fromAddress = viewFromAddress(searchParams, columns)
  const addressKey = viewKey(fromAddress)

  const view = useTableView(rows, columns, {
    initial: fromAddress,
    rank,
    onChange: (state) => setSearchParams((current) => withView(current, state), { replace: true }),
  })

  const { replace } = view
  const shownKey = viewKey(view.state)

  useEffect(() => {
    if (addressKey !== shownKey) {
      replace(viewFromAddress(new URLSearchParams(searchParams), columns))
    }
    // Only an address that changes is followed; the view's own changes are written, not read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressKey])

  return view
}
